import {AfterViewChecked, Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {MatDialog, MatDialogConfig, MatDialogRef} from '@angular/material/dialog';
import {CommentDialogComponent} from '../../../dialogs/comment-dialog/comment-dialog.component';
import {TranslateService} from '@ngx-translate/core';
import {CommentBasicModel} from '@core/modules/rest/consent/response/comment-basic.model';
import {DeleteConfirmationDialogComponent} from '../../../dialogs/delete-confirmation-dialog/delete-confirmation-dialog.component';
import {EditCommentRequestModel} from '@core/modules/rest/consent/request/edit-comment-request.model';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {DeleteDialogModel} from '../../../dialogs/delete-confirmation-dialog/model/delete-dialog.model';

@UntilDestroy()
@Component({
    selector: 'app-display-consent-comments',
    templateUrl: './display-consent-comments.component.html',
    styleUrls: ['./display-consent-comments.component.scss']
})
export class DisplayConsentCommentsComponent implements OnInit, AfterViewChecked, OnChanges {
    @ViewChild('commentHolderDiv') commentHolderDiv: ElementRef | undefined;
    @Input() sideMenuOpen: boolean;
    @Input() comments: CommentBasicModel[] | undefined;
    @Input() hideButtonActionsForReviewer = false;
    @Output() addCommentEvent = new EventEmitter<string>();
    @Output() editEvent: EventEmitter<EditCommentRequestModel> = new EventEmitter<EditCommentRequestModel>();
    @Output() deleteEvent: EventEmitter<string> = new EventEmitter<string>();
    @Output() pauseAudioEvent = new EventEmitter();
    disableScrollDown = false;
    commentDeletedOrEdited = false;

    constructor(private dialog: MatDialog, private translate: TranslateService) {
    }

    ngOnInit(): void {
        this.comments = this.comments?.slice().sort((a, b) => (a.createdAt > b.createdAt) ? 1 : -1);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.comments && this.comments && !this.commentDeletedOrEdited) {
            this.disableScrollDown = false;
        }
    }

    ngAfterViewChecked(): void {
        this.scrollToBottom();
    }

    addComment(): void {
        this.commentDeletedOrEdited = false;
        const objData = {
            title: this.translate.instant('CONSENT.COMMENTS.ADD_NEW_QUESTION'),
            comment: null
        };
        const dialogRef = this.prepareDialogConfig(CommentDialogComponent, objData);
        dialogRef.componentInstance.commentEvent.pipe(untilDestroyed(this)).subscribe((data: string) => {
            this.addCommentEvent.emit(data);
        });
    }

    editComment(commentId: string): void {
        this.commentDeletedOrEdited = true;
        const com = this.comments?.find(item => item.id === commentId);
        if (com) {
            const objData = {
                title: this.translate.instant('CONSENT.COMMENTS.EDIT_COMMENT'),
                comment: com
            };
            const dialogRef = this.prepareDialogConfig(CommentDialogComponent, objData);
            dialogRef.componentInstance.editEvent.pipe(untilDestroyed(this)).subscribe((data: string) => {
                const editObj: EditCommentRequestModel = {
                    id: commentId,
                    content: data
                };
                this.editEvent.emit(editObj);
            });
        }
    }

    deleteComment(commentId: string): void {
        this.commentDeletedOrEdited = true;
        const tempCom = this.comments?.find(item => item.id === commentId);
        let data: DeleteDialogModel;
        if (tempCom) {
            data = {
                title: this.translate.instant('CONSENT.COMMENTS.DELETE.DIALOG_TITLE'),
                description: this.translate.instant('CONSENT.COMMENTS.DELETE.DIALOG_DESCRIPTION', {unitTitle: tempCom.informationUnitTitle})
            };
        }
        this.pauseAudioEvent.emit();
        const dialogRef = this.prepareDialogConfig(DeleteConfirmationDialogComponent, data);
        dialogRef.componentInstance.closeDialogEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.close();
        });
        dialogRef.componentInstance.deleteCommentEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.close();
            this.deleteEvent.emit(commentId);
        });
    }

    prepareDialogConfig(currentDialog: any, dataObject?: any): MatDialogRef<any> {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.autoFocus = true;
        dialogConfig.disableClose = true;
        dialogConfig.restoreFocus = true;
        if (dataObject) {
            dialogConfig.data = dataObject;
        }
        if (navigator.userAgent.match(/iPhone|iPod|(iPad)/) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
            dialogConfig.position = {
                top: '0'
            };
        }
        return this.dialog.open(currentDialog, dialogConfig);
    }

    scrollToBottom(): void {
        if (!this.disableScrollDown && this.commentHolderDiv) {
            try {
                this.commentHolderDiv.nativeElement.scrollTop = this.commentHolderDiv?.nativeElement.scrollHeight;
            } catch (err) {
            }
        }
    }

    onScroll(): void {
        if (this.commentHolderDiv) {
            const element = this.commentHolderDiv.nativeElement;
            const atBottom = element.scrollHeight - element.scrollTop === element.clientHeight;
            this.disableScrollDown = !(this.disableScrollDown && atBottom);
        }
    }
}
